import {Injectable} from '@angular/core';
import {Navigate} from '@ngxs/router-plugin';
import {Action, State, StateContext, StateToken, Selector, NgxsOnInit} from '@ngxs/store';
import {Observable, throwError} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {StateResetAll} from 'ngxs-reset-plugin';
import {Oferta, OfertaListFilter} from "../models/oferta.model";
import {PageableApiResult} from "../models/pagable-api-result.model";
import {Transaccion} from "../models/transaccion.model";
import {OfertaApiService} from "../api/oferta.api.service";
import {
  AceptarOfertaAction,
  DeclinarOfertaAction, DeclinarOfertaEnviadaAction,
  GetOfertaAction,
  SearchOfertaRealizadaListAction,
  SearchOfertaRecibidaListAction
} from "../actions/ofertas.actions";

export interface OfertaStateModel {
  ofertaRecibidaList: PageableApiResult<Oferta>,
  ofertaRecibidaListFilter: OfertaListFilter,
  ofertaRealizadaList: PageableApiResult<Oferta>,
  ofertaRealizadaListFilter: OfertaListFilter,
  oferta: Oferta,
  ofertaDetail: any,
  declinarOfertaError:boolean,
  declinarOfertaEnviadaError:boolean,
  aceptarOfertaError:boolean,
  transaccion: Transaccion,
}

const initialState: OfertaStateModel = {
  oferta: null,
  ofertaRealizadaList: null,
  ofertaRealizadaListFilter:null,
  ofertaRecibidaList: null,
  ofertaRecibidaListFilter: null,
  ofertaDetail:null,
  declinarOfertaError:null,
  declinarOfertaEnviadaError:null,
  aceptarOfertaError:null,
  transaccion: null,
};

const OFERTA_STATE_TOKEN = new StateToken<OfertaStateModel>('oferta');

@State<OfertaStateModel>({
  name: OFERTA_STATE_TOKEN,
  defaults: initialState
})
@Injectable()
export class OfertaState {

  constructor(
    private ofertaApiService: OfertaApiService
  ) {
  }

  @Action(SearchOfertaRecibidaListAction)
  handleSearchOfertaRecibidaListAction(ctx: StateContext<OfertaStateModel>, action: SearchOfertaRecibidaListAction) {
    // Check for status options in state
    const filter = ctx.getState()?.ofertaRecibidaListFilter;
    const actionFilter = action.payload.filter

    let reqFilter: OfertaListFilter

    if (filter) {

      reqFilter = {
        ...filter,
        ...actionFilter
      }

    } else {
      //filtros por defecto:
      reqFilter = {
        page: 0,
        size: 10,
        dateFilter: actionFilter?.dateFilter,
        sortByFecha:null,
        sortByMonto:null,
        status: null,
      }
    }

    ctx.patchState({
      ofertaRecibidaListFilter: reqFilter
    })

    return this.ofertaApiService.searchOfertaRecibidaList(reqFilter).pipe(
      tap(data => {
        ctx.patchState({
          ofertaRecibidaList: data
        })
      })
    )
  }

  @Action(SearchOfertaRealizadaListAction)
  handleSearchOfertaRealizadaListAction(ctx: StateContext<OfertaStateModel>, action: SearchOfertaRealizadaListAction) {
    // Check for status options in state
    const filter = ctx.getState()?.ofertaRealizadaListFilter;
    const actionFilter = action.payload.filter;

    let reqFilter: OfertaListFilter;

    if (filter) {

      reqFilter = {
        ...filter,
        ...actionFilter
      };

    } else {
      // filtros por defecto:
      reqFilter = {
        page: 0,
        size: 10,
        dateFilter: null,
        numero: null,
        sortByFecha: null,
        sortByMonto: null,
        status: null
      };
    }

    ctx.patchState({
      ofertaRealizadaListFilter: reqFilter
    })

    return this.ofertaApiService.searchOfertaRealizadaList(reqFilter).pipe(
      tap(data => {
        ctx.patchState({
          ofertaRealizadaList: data
        })
      })
    )
  }

  @Action(AceptarOfertaAction)
  handleAceptarOfertaAction(ctx: StateContext<OfertaStateModel>, action: AceptarOfertaAction) {
    // Check for status options in state
    const idOferta = action.payload.ofertaId;

    return this.ofertaApiService.aceptarOferta(idOferta).pipe(
      tap(rawResponse => {
        if(rawResponse.status == 204) {
          ctx.patchState({
            aceptarOfertaError: true
          })
        } else {
          ctx.patchState({
            aceptarOfertaError: false,
            transaccion: rawResponse.body
          })
        }
      }),catchError(err => {
        ctx.patchState({
          aceptarOfertaError: true
        })
        return throwError(err);
      })
    )
  }

  @Action(DeclinarOfertaAction)
  handleDeclinarOfertaAction(ctx: StateContext<OfertaStateModel>, action: DeclinarOfertaAction) {
    // Check for status options in state
    const idOferta = action.payload.ofertaId;

    return this.ofertaApiService.declinarOferta(idOferta).pipe(
      tap(rawResponse => {
        if(rawResponse.status == 204) {
          ctx.patchState({
            declinarOfertaError: true
          })
        } else {
          ctx.patchState({
            declinarOfertaError: false
          })
        }
      }),catchError(err => {
        ctx.patchState({
          declinarOfertaError: true
        })
        return throwError(err);
      })
    )
  }

  @Action(DeclinarOfertaEnviadaAction)
  handleDeclinarOfertaEnviadaAction(ctx: StateContext<OfertaStateModel>, action: DeclinarOfertaEnviadaAction) {
    // Check for status options in state
    const idOferta = action.payload.ofertaId;

    return this.ofertaApiService.declinarOfertaEnviada(idOferta).pipe(
      tap(rawResponse => {
        if(rawResponse.status == 204) {
          ctx.patchState({
            declinarOfertaEnviadaError: true
          })
        } else {
          ctx.patchState({
            declinarOfertaEnviadaError: false
          })
        }
      }),catchError(err => {
        ctx.patchState({
          declinarOfertaEnviadaError: true
        })
        return throwError(err);
      })
    )
  }

  @Action(GetOfertaAction)
  handleGetOfertaAction(ctx: StateContext<OfertaStateModel>, action: GetOfertaAction) {
    // Check for status options in state
    const idOferta = action.payload.ofertaId;

    return this.ofertaApiService.getOfertaById(idOferta).pipe(
      tap(data => {
        ctx.patchState({
          ofertaDetail: data
        })
      })
    )
  }

  @Selector()
  static getOfertasRecibidas(state: OfertaStateModel): PageableApiResult<Oferta> {
    return state?.ofertaRecibidaList
  }

  @Selector()
  static getOfertasRealizadas(state: OfertaStateModel): PageableApiResult<Oferta> {
    return state?.ofertaRealizadaList
  }

}
